import { DestroyRef, Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, tap } from 'rxjs';
import { LanguageService } from '../language/language.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { Langs } from '../language/language.types';
import { IMAGE_META_MAP } from './meta.constants';
import { META_TRANSLATIONS } from './meta.types';

@Injectable({ providedIn: 'root' })
export class MetaService {
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly lang = inject(LanguageService);
  private readonly router = inject(Router);

  public init(destroyRef: DestroyRef) {
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    return this.router.events
      .pipe(
        filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd),
        map((ev) => ev.urlAfterRedirects.split('?')[0].split('#')[0]),
        distinctUntilChanged(),
        tap((url) => {
          const getPath = (
            path: string,
          ): keyof typeof META_TRANSLATIONS | null => {
            const metaKeys = Object.keys(META_TRANSLATIONS);
            const hasPath = metaKeys.includes(path);
            if (!hasPath) {
              console.warn(`Not available path: ${path} in keys ${metaKeys}`);
              return null;
            }
            return path as keyof typeof META_TRANSLATIONS;
          };

          const [_, language, path, thirdParam] = url.split('/');
          const URL = environment.origin + url;
          this.meta.updateTag({ property: 'og:url', content: URL });
          this.meta.updateTag({ name: 'url', content: URL });

          const controledPath = getPath(path ?? '');
          if (!(controledPath === 'blog' && thirdParam)) {
            this.setMetaForPath(controledPath, language as Langs);
          }
        }),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe();
  }

  public setMeta(
    title: string,
    description: string,
    image: string,
    keywords?: string | null,
    ogTitle?: string | null,
    ogDescription?: string | null,
  ) {
    this.titleService.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: ogTitle ?? title });
    this.meta.updateTag({
      property: 'og:description',
      content: ogDescription ?? description,
    });

    const imageContent = this.getImageContent(image);
    this.meta.updateTag({ property: 'og:image', content: imageContent });
    this.meta.updateTag({ property: 'twitter:image', content: imageContent });

    if (keywords?.trim().length) {
      this.meta.updateTag({ name: 'keywords', content: keywords });
    } else {
      this.meta.removeTag('keywords');
    }
  }

  private setMetaForPath(
    path: keyof typeof META_TRANSLATIONS | null,
    language: Langs,
  ) {
    if (path === null) return;
    const title = this.lang.instant(
      'meta.' + META_TRANSLATIONS[path] + '.title',
      null,
    );
    const description = this.lang.instant(
      'meta.' + META_TRANSLATIONS[path] + '.description',
      null,
    );
    const ogTitle = this.lang.instant(
      'meta.' + META_TRANSLATIONS[path] + '.ogtitle',
      null,
    );
    const ogDescription = this.lang.instant(
      'meta.' + META_TRANSLATIONS[path] + '.ogdescription',
      null,
    );
    const keywords = this.lang.instant(
      'meta.' + META_TRANSLATIONS[path] + '.keywords',
      null,
    );

    const langWithPathImage =
      IMAGE_META_MAP[language]?.[META_TRANSLATIONS[path]];
    const pathImage = IMAGE_META_MAP.en[META_TRANSLATIONS[path]];
    const image = langWithPathImage ?? pathImage ?? 'en-default-meta';

    if (title && description) {
      this.setMeta(title, description, image, keywords, ogTitle, ogDescription);
    } else {
      this.setDefaultMeta();
    }
  }

  private setDefaultMeta() {
    this.setMeta(
      this.lang.instant('meta.default.title'),
      this.lang.instant('meta.default.description'),
      'en-default-meta',
      this.lang.instant('meta.default.keywords', null),
      this.lang.instant('meta.default.ogtitle', null),
      this.lang.instant('meta.default.ogdescription', null),
    );
  }

  private getImageContent(image: string) {
    return image.startsWith('http')
      ? image
      : `${environment.origin}/assets/img/Meta/${image}.png`;
  }
}
